import { Divider, flex, IconFont } from '@byecode/ui'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { RichTextContentProtocol, VariableADTvalue } from '@lighthouse/core'
import { type PageAbstract } from '@lighthouse/core'
import { fieldConvertValue, generateText, TagIcon } from '@lighthouse/shared'
import { Collapse } from '@mantine/core'
import React, { useCallback, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import { EventList } from '@/components/EventList'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'

import { ButtonItemConfigure } from './ButtonItemConfigure'
import * as SC from './styles'

const SCxRightFill = styled.div`
    width: 180px;
    /* max-width: 80%; */
    ${flex}
    justify-content: flex-end;
    align-items: center;
    gap: 7px;
`

const SCxListItemAction = styled.div`
    padding: 0 8px;
`

interface ButtonSettingProps {
    disableViewOption?: boolean
    opened: boolean
    index?: number
    prefixName?: string
    disabled?: boolean
    themeColor: string
    hideConfigs?: {
        fillWay?: boolean
    }
    onChangeOpen: (opened: boolean) => void
    onDelete: (id: string) => void
}

export const ButtonSettingItem: React.FunctionComponent<ButtonSettingProps> = ({
    disableViewOption,
    opened,
    index,
    prefixName,
    hideConfigs,
    disabled,
    onChangeOpen,
    onDelete
}) => {
    const { watch, control } = useFormContext()
    const { name, id, action } = watch(`${prefixName}.${index}`)

    const { attributes, listeners, setNodeRef, transform, transition, active, over } = useSortable({
        id
    })

    const style: React.CSSProperties = useMemo(
        () => ({
            transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
            transition,
            zIndex: active?.id === id ? 1 : 0
        }),
        [active?.id, id, transform, transition]
    )

    const parseVariable = useParseVariableWithSubscription(name)
    const parseVariable2String = useCallback(
        (value: VariableADTvalue) => {
            const field = parseVariable(value)
            if (!field) {
                return ''
            }

            return fieldConvertValue(field)
        },
        [parseVariable]
    )
    const handleRenderTitle = useCallback(
        (v: RichTextContentProtocol) => {
            return generateText(v, { variable: { renderLabel: v => parseVariable2String(v.attrs.value) } })
        },
        [parseVariable2String]
    )

    return (
        <SC.ButtonContainer {...attributes} style={style} ref={setNodeRef}>
            <SC.Header>
                <SC.LeftFill>
                    <SC.Handle {...listeners} style={{ cursor: 'grab' }}>
                        <IconFont size={16} type="DotsSix" color="var(--color-gray-500)" />
                    </SC.Handle>
                    <SC.Title> {handleRenderTitle(name)}</SC.Title>
                </SC.LeftFill>
                <SCxRightFill style={{ width: 'auto' }}>
                    <TagIcon icon="Trash" iconColor="var(--color-gray-500)" onClick={() => onDelete?.(id)} />
                    <IconFont
                        size={16}
                        color="var(--color-gray-500)"
                        type={opened ? 'ArrowUpSmall' : 'ArrowDownSmall'}
                        onClick={() => onChangeOpen(!opened)}
                    />
                </SCxRightFill>
            </SC.Header>
            <Collapse in={opened}>
                <SC.Content>
                    <ButtonItemConfigure
                        hideConfigs={hideConfigs}
                        prefixName={prefixName}
                        index={index}
                        disableViewOption={disableViewOption}
                    />
                    <Divider color="var(--color-gray-200)" style={{ margin: '12px 0' }} />
                    <Controller
                        control={control}
                        name={`${prefixName}.${index}.eventList`}
                        render={({ field }) => (
                            <EventList style={{ padding: 0 }} blockType="button" value={field.value} onChange={field.onChange} />
                        )}
                    />
                </SC.Content>
            </Collapse>
        </SC.ButtonContainer>
    )
}
